import { apiFetch } from '../utils/apiClient';
import { isGeminiScoutingReportEnabled, isMotionReviewNotesEnabled } from '../config/featureFlags';

export const SCOUTING_REPORT_V2_VERSION = 'scouting_report_v2.v1';

export const SCOUTING_REPORT_V2_SECTIONS = [
  'executive_summary',
  'offensive_identity',
  'defensive_identity',
  'passing_game_tendencies',
  'run_game_tendencies',
  'motion_formation_tendencies',
  'pressure_coverage_tendencies',
  'situational_football',
  'red_zone_tendencies',
  'third_down_tendencies',
  'exploit_recommendations',
  'suggested_game_plan',
  'in_game_adjustment_triggers',
  'confidence_sample_notes',
] as const;

export type ScoutingReportV2SectionKey = (typeof SCOUTING_REPORT_V2_SECTIONS)[number];

export type ScoutingReportConfidence = 'low' | 'moderate' | 'high';

export interface ScoutingReportV2Section {
  key: ScoutingReportV2SectionKey;
  title: string;
  what_we_saw: string[];
  why_it_matters: string[];
  how_to_attack: string[];
  confidence: ScoutingReportConfidence;
  sample_note: string | null;
  evidence_refs: string[];
}

export interface ScoutingReportV2 {
  version: string;
  status: string;
  generated_at?: string;
  sections: ScoutingReportV2Section[];
}

const _CONFIDENCE_VALUES: ScoutingReportConfidence[] = ['low', 'moderate', 'high'];

function sanitizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function parseScoutingReportV2Section(
  key: ScoutingReportV2SectionKey,
  value: unknown
): ScoutingReportV2Section | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const title = typeof record.title === 'string' ? record.title.trim() : '';
  if (!title) return null;

  const confidenceRaw = typeof record.confidence === 'string' ? record.confidence : 'low';
  const confidence = _CONFIDENCE_VALUES.includes(confidenceRaw as ScoutingReportConfidence)
    ? (confidenceRaw as ScoutingReportConfidence)
    : 'low';

  const sampleNote =
    typeof record.sample_note === 'string' && record.sample_note.trim()
      ? record.sample_note.trim()
      : null;

  return {
    key,
    title,
    what_we_saw: sanitizeStringList(record.what_we_saw),
    why_it_matters: sanitizeStringList(record.why_it_matters),
    how_to_attack: sanitizeStringList(record.how_to_attack),
    confidence,
    sample_note: sampleNote,
    evidence_refs: sanitizeStringList(record.evidence_refs),
  };
}

/**
 * Defensive client-side parse of scouting_report_v2 delivered on the /full or /status
 * projection. Returns null unless the frontend flag is on and the payload is well-formed.
 * Only known section keys and safe fields survive; unknown/internal keys are dropped.
 */
export function parseScoutingReportV2(value: unknown): ScoutingReportV2 | null {
  if (!isGeminiScoutingReportEnabled()) return null;
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.version !== SCOUTING_REPORT_V2_VERSION) return null;
  if (record.status !== 'generated') return null;
  const rawSections = record.sections;
  if (!rawSections || typeof rawSections !== 'object') return null;

  const sectionMap = rawSections as Record<string, unknown>;
  const sections: ScoutingReportV2Section[] = [];
  for (const key of SCOUTING_REPORT_V2_SECTIONS) {
    const parsed = parseScoutingReportV2Section(key, sectionMap[key]);
    if (parsed) sections.push(parsed);
  }
  if (sections.length === 0) return null;

  return {
    version: SCOUTING_REPORT_V2_VERSION,
    status: 'generated',
    generated_at:
      typeof record.generated_at === 'string' ? record.generated_at : undefined,
    sections,
  };
}

export function hasRenderableScoutingReportV2(
  report: ScoutingReportV2 | null | undefined
): report is ScoutingReportV2 {
  return Boolean(report && report.sections && report.sections.length > 0);
}

export const DEFAULT_MOTION_REVIEW_NOTES_DISCLAIMER =
  'These notes are reviewed motion indicators from the film sample. They are directional scouting clues, not confirmed play calls.';

export interface MotionReviewNotesItem {
  statement: string;
  concept_display: string;
  attack_area_display: string;
  concept_explanation?: string;
}

export interface MotionReviewNotesPayload {
  upload_id: string;
  section_key: string;
  title: string;
  disclaimer: string;
  items: MotionReviewNotesItem[];
}

function sanitizeMotionReviewNotesItem(value: unknown): MotionReviewNotesItem | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.statement !== 'string' || !record.statement.trim()) {
    return null;
  }
  if (typeof record.concept_display !== 'string' || !record.concept_display.trim()) {
    return null;
  }
  if (typeof record.attack_area_display !== 'string' || !record.attack_area_display.trim()) {
    return null;
  }
  const item: MotionReviewNotesItem = {
    statement: record.statement.trim(),
    concept_display: record.concept_display.trim(),
    attack_area_display: record.attack_area_display.trim(),
  };
  if (typeof record.concept_explanation === 'string' && record.concept_explanation.trim()) {
    item.concept_explanation = record.concept_explanation.trim();
  }
  return item;
}

export function parseMotionReviewNotesPayload(body: unknown): MotionReviewNotesPayload | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }
  const items = record.items
    .map(sanitizeMotionReviewNotesItem)
    .filter((item): item is MotionReviewNotesItem => item !== null);
  if (items.length === 0) {
    return null;
  }
  return {
    upload_id: typeof record.upload_id === 'string' ? record.upload_id : '',
    section_key:
      typeof record.section_key === 'string' ? record.section_key : 'motion_review_notes',
    title: typeof record.title === 'string' ? record.title : 'Motion Review Notes',
    disclaimer:
      typeof record.disclaimer === 'string' && record.disclaimer.trim()
        ? record.disclaimer.trim()
        : DEFAULT_MOTION_REVIEW_NOTES_DISCLAIMER,
    items,
  };
}

export const MOTION_REVIEW_NOTES_MAX_ATTEMPTS = 4;
export const MOTION_REVIEW_NOTES_RETRY_DELAY_MS = 600;

type MotionReviewNotesAttemptOutcome =
  | { kind: 'success'; payload: MotionReviewNotesPayload }
  | { kind: 'terminal' }
  | { kind: 'retryable' };

async function attemptFetchMotionReviewNotes(
  uploadId: string
): Promise<MotionReviewNotesAttemptOutcome> {
  try {
    const response = await apiFetch(
      `/api/analysis/${encodeURIComponent(uploadId)}/motion-review-notes`
    );
    if (response.status === 404) {
      return { kind: 'terminal' };
    }
    if (!response.ok) {
      return { kind: 'retryable' };
    }
    const body = await response.json();
    const payload = parseMotionReviewNotesPayload(body);
    if (!payload) {
      return { kind: 'terminal' };
    }
    return { kind: 'success', payload };
  } catch {
    return { kind: 'retryable' };
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function fetchMotionReviewNotes(
  uploadId: string
): Promise<MotionReviewNotesPayload | null> {
  const outcome = await attemptFetchMotionReviewNotes(uploadId);
  return outcome.kind === 'success' ? outcome.payload : null;
}

export interface ResolveMotionReviewNotesOptions {
  onRetry?: (attempt: number) => void;
  signal?: AbortSignal;
  /** Test hook: override retry delay (production uses MOTION_REVIEW_NOTES_RETRY_DELAY_MS). */
  retryDelayMs?: number;
}

export async function fetchMotionReviewNotesWithRetry(
  uploadId: string,
  options?: ResolveMotionReviewNotesOptions
): Promise<MotionReviewNotesPayload | null> {
  const retryDelayMs = options?.retryDelayMs ?? MOTION_REVIEW_NOTES_RETRY_DELAY_MS;

  for (let attempt = 1; attempt <= MOTION_REVIEW_NOTES_MAX_ATTEMPTS; attempt += 1) {
    if (options?.signal?.aborted) {
      return null;
    }

    const outcome = await attemptFetchMotionReviewNotes(uploadId);
    if (outcome.kind === 'success') {
      return outcome.payload;
    }
    if (outcome.kind === 'terminal') {
      return null;
    }

    if (attempt < MOTION_REVIEW_NOTES_MAX_ATTEMPTS) {
      options?.onRetry?.(attempt);
      await sleep(retryDelayMs);
    }
  }

  return null;
}

export async function resolveReportMotionReviewNotes(
  uploadId: string | undefined,
  isCompleted: boolean,
  options?: ResolveMotionReviewNotesOptions
): Promise<MotionReviewNotesPayload | null> {
  if (!uploadId || !isCompleted || !isMotionReviewNotesEnabled()) {
    return null;
  }
  return fetchMotionReviewNotesWithRetry(uploadId, options);
}
